import { useCart } from '@shopify/hydrogen-react'
import { Money } from '@shopify/hydrogen-react'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { AspectRatio } from '@/components/primitives/AspectRatio/AspectRatio'
import { Dropdown } from '@/components/primitives/Dropdown/Dropdown'
import { Quantity } from '@/components/primitives/Quantity/Quantity'

import { DEHIRE_REASON_OPTIONS, IMAGE_SIZE, CART_DEBOUNCE_TIME } from './Cart.constants'

import type { CartLineType } from './Cart.types'
import type { AttributeInput } from '@shopify/hydrogen-react/storefront-api-types'

// Business Logic - SUP0003-498
// 1. A customer cannot order more than their current stock holding. i.e. Order Quantity <= Held Quantity.
// 2. A customer cannot return more than their current stock holding. i.e. Return Quantity <= Held Quantity.
// 3. When ordering, the return quantity should default to the same value as their order quantity. Anytime the customer adjusts their order quantity, automatically re-align the return quantity to that order value. Customers can increase the return quantity thereafter.
// 4. Customers can return more cylinders than they order. They must provide a de-hire reason.
export const RentalCartLineItem = ({ line }: { line: CartLineType }) => {
  const cart = useCart()

  const totalStockholding = Number(line?.attributes?.find((attribute) => attribute?.key === '_total_stockholding')?.value)
  const hireQuantity = Number(line?.attributes?.find((attribute) => attribute?.key === '_hire_quantity')?.value)
  const dehireQuantity = Number(line?.attributes?.find((attribute) => attribute?.key === '_dehire_quantity')?.value)
  const dehireReason = line?.attributes?.find((attribute) => attribute?.key === '_dehire_reason')?.value || ''
  const totalPrice = Number(line?.cost?.totalAmount?.amount)
  const subTotalPrice = Number(line?.cost?.subtotalAmount?.amount)
  const quantity = Number(line?.quantity)

  const isDiscounted = totalPrice < subTotalPrice

  const [localState, setLocalState] = useState({ hireQty: hireQuantity, dehireQty: dehireQuantity, dehireReason })

  // Sync local state when cart attributes change (e.g. external updates, post-API response)
  useEffect(() => {
    setLocalState({ hireQty: hireQuantity, dehireQty: dehireQuantity, dehireReason })
  }, [hireQuantity, dehireQuantity, dehireReason])

  const isDehiring = localState.dehireQty > localState.hireQty

  const debouncedHireCartUpdate = useDebouncedCallback((quantity: number, effectiveDehireQty: number, linked: boolean) => {
    if (!line || !line.id || !line.attributes || !line.attributes.length) return
    const attributes = line.attributes.filter((attribute) => attribute && attribute.key && attribute.value) as AttributeInput[]

    if (effectiveDehireQty === 0 && quantity === 0) {
      cart?.linesRemove([line.id])

      return
    }

    attributes.forEach((attribute) => {
      if (attribute.key === '_hire_quantity') {
        attribute.value = quantity.toString()
      }

      if (linked && attribute.key === '_dehire_quantity') {
        attribute.value = effectiveDehireQty.toString()
      }
    })

    // If we are changing quantity >= 1, the quantity and _hire_quantity should be the same
    if (quantity >= 1) {
      cart?.linesUpdate([
        {
          id: line.id,
          quantity,
          attributes,
        },
      ])
    } else {
      // If we are changing quantity to 0, the quantity should be 1 and _hire_quantity should be 0
      cart?.linesUpdate([
        {
          id: line.id,
          quantity: 1,
          attributes,
        },
      ])
    }
  }, CART_DEBOUNCE_TIME)

  const handleHireQuantityChange = (quantity: number) => {
    const isLinked = localState.hireQty === localState.dehireQty
    const newDehireQty = isLinked ? Math.min(quantity, totalStockholding) : localState.dehireQty
    setLocalState((prev) => ({ ...prev, hireQty: quantity, dehireQty: newDehireQty }))
    debouncedHireCartUpdate(quantity, newDehireQty, isLinked)
  }

  const debouncedDehireCartUpdate = useDebouncedCallback((quantity: number, currentHireQty: number) => {
    if (!line || !line.id || !line.attributes || !line.attributes.length) return
    const attributes = line.attributes.filter((attribute) => attribute && attribute.key && attribute.value) as AttributeInput[]

    // Do not allow the dehire quantity to be greater than the total stockholding
    if (quantity > totalStockholding) return

    // If we are setting the dehire quantity to 0, and the hire quantity is 0, we need to remove the line item
    if (currentHireQty === 0 && quantity === 0) {
      cart?.linesRemove([line.id])

      return
    }

    attributes.forEach((attribute) => {
      if (attribute.key === '_dehire_quantity') {
        attribute.value = quantity.toString()
      }
    })

    cart?.linesUpdate([
      {
        id: line.id,
        attributes,
      },
    ])
  }, CART_DEBOUNCE_TIME)

  const handleDehireQuantityChange = (quantity: number) => {
    setLocalState((prev) => ({ ...prev, dehireQty: quantity }))
    debouncedDehireCartUpdate(quantity, localState.hireQty)
  }

  if (!line || !quantity) return <></>

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4">
        {line?.merchandise?.image?.url && (
          <div className="size-14">
            <AspectRatio ratio="1/1">
              <div className="relative size-full overflow-hidden rounded-sm border border-grey-300">
                <Image
                  src={line.merchandise.image.url}
                  alt={line.merchandise.image.altText || line.merchandise.title || ''}
                  fill
                  sizes={IMAGE_SIZE}
                  className="object-cover object-center"
                />
              </div>
            </AspectRatio>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div>
            <p className="font-medium">{line?.merchandise?.product?.title}</p>
            <div className="flex items-center gap-1">
              {line?.cost?.totalAmount && <Money data={line?.cost?.totalAmount} />}
              {line?.cost?.subtotalAmount && hireQuantity > 0 && isDiscounted && <Money className="text-grey-400 line-through" data={line?.cost?.subtotalAmount} />}
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex w-1/2 flex-col gap-2">
          <p className="text-sm font-medium text-grey-700">Order QTY</p>
          <Quantity value={localState.hireQty} maxNumber={totalStockholding} onChange={handleHireQuantityChange} />
        </div>

        <div className="flex w-1/2 flex-col gap-2">
          <p className="text-sm font-medium text-grey-700">Return QTY</p>
          <Quantity value={localState.dehireQty} maxNumber={totalStockholding} onChange={handleDehireQuantityChange} minNumber={localState.hireQty} />
        </div>
      </div>
      {isDehiring && (
        <Dropdown
          options={DEHIRE_REASON_OPTIONS}
          label="Dehire Reason*"
          defaultValue={localState.dehireReason}
          onChange={(value) => {
            setLocalState((prev) => ({ ...prev, dehireReason: value }))
            if (!line.id || !line.attributes || !line.attributes.length) return
            const attributes = line.attributes.filter((attribute) => attribute && attribute.key) as AttributeInput[]

            attributes.forEach((attribute) => {
              if (attribute.key === '_dehire_reason') {
                attribute.value = value
              }
            })

            cart?.linesUpdate([
              {
                id: line.id,
                attributes,
              },
            ])
          }}
        />
      )}
    </div>
  )
}
